import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  IdleDisplay,
  SyncIntervalSeconds,
  UpdateOrganisationSettingsRequest,
} from '@ghostscreen/contracts';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { useTheme } from '../theme/ThemeProvider';
import { ProfileSection } from './settings/ProfileSection';
import { SettingsChipGroup } from './settings/SettingsChipGroup';
import { SettingsRow } from './settings/SettingsRow';
import { SettingsSelect } from './settings/SettingsSelect';
import {
  IDLE_DISPLAY_VALUES,
  SETTINGS_TIMEZONES,
  SYNC_INTERVAL_VALUES,
} from './settings/settingsOptions';

/**
 * Settings — personal profile, organisation playback prefs + dashboard appearance
 * (Ghost Screen Settings.dc.html). Persisted server-side except theme.
 */
export function SettingsPage() {
  const { t } = useTranslation('settings');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const [toast, setToast] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['organisation-settings', organisationId],
    queryFn: () => api.getOrganisationSettings(organisationId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const alertsQuery = useQuery({
    queryKey: ['alert-settings', organisationId],
    queryFn: () => api.getAlertSettings(organisationId),
  });

  const retry = useQueryRetry(async () => {
    await Promise.all([
      settingsQuery.refetch(),
      playlistsQuery.refetch(),
      alertsQuery.refetch(),
    ]);
  });

  const saveSettings = useMutation({
    mutationFn: (body: UpdateOrganisationSettingsRequest) =>
      api.updateOrganisationSettings(organisationId, body),
    onSuccess: async (_data, body) => {
      await queryClient.invalidateQueries({
        queryKey: ['organisation-settings', organisationId],
      });
      if (body.timezone !== undefined) {
        await queryClient.invalidateQueries({ queryKey: ['locations', organisationId] });
        setToast(t('timezone.saved', { timezone: body.timezone }));
        return;
      }
      if (body.defaultPlaylistId !== undefined) {
        if (body.defaultPlaylistId === null) {
          setToast(t('defaultPlaylist.savedNothing'));
          return;
        }
        const name =
          playlistsQuery.data?.find((playlist) => playlist.id === body.defaultPlaylistId)?.name ||
          t('defaultPlaylist.untitled');
        setToast(t('defaultPlaylist.saved', { name }));
        return;
      }
      if (body.syncIntervalSeconds !== undefined) {
        const interval = t(`syncInterval.options.${body.syncIntervalSeconds}`);
        setToast(t('syncInterval.saved', { interval }));
        return;
      }
      if (body.idleDisplay !== undefined) {
        setToast(t(`idleDisplay.saved.${body.idleDisplay}`));
      }
    },
  });

  const saveAlerts = useMutation({
    mutationFn: (emailAlertsEnabled: boolean) =>
      api.updateAlertSettings(organisationId, { emailAlertsEnabled }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alert-settings', organisationId] });
      setToast(t('alerts.saved'));
    },
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const playlistOptions = useMemo(
    () => [
      { value: '', label: t('defaultPlaylist.nothing') },
      ...(playlistsQuery.data ?? []).map((playlist) => ({
        value: playlist.id,
        label: playlist.name || t('defaultPlaylist.untitled'),
      })),
    ],
    [playlistsQuery.data, t],
  );

  const timezoneOptions = useMemo(() => {
    const current = settingsQuery.data?.timezone;
    const base = SETTINGS_TIMEZONES.map((value) => ({ value, label: value }));
    if (current && !SETTINGS_TIMEZONES.includes(current as (typeof SETTINGS_TIMEZONES)[number])) {
      return [{ value: current, label: current }, ...base];
    }
    return [...base];
  }, [settingsQuery.data?.timezone]);

  const syncIntervalOptions = SYNC_INTERVAL_VALUES.map((value) => ({
    value,
    label: t(`syncInterval.options.${value}`),
  }));
  const idleDisplayOptions = IDLE_DISPLAY_VALUES.map((value) => ({
    value,
    label: t(`idleDisplay.options.${value}`),
  }));

  const loading =
    settingsQuery.isPending || playlistsQuery.isPending || alertsQuery.isPending;
  const failed =
    settingsQuery.isError || playlistsQuery.isError || alertsQuery.isError;
  const busy = saveSettings.isPending || saveAlerts.isPending;
  const settings = settingsQuery.data;
  const failedSave = saveSettings.isError ? saveSettings.error : saveAlerts.error;
  const saveError =
    saveSettings.isError || saveAlerts.isError
      ? apiErrorMessage(failedSave, t('saveError'))
      : null;

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-4 self-stretch"
      style={{ animation: 'gs-in 260ms ease both', width: '100%' }}
    >
      <ProfileSection onSaved={setToast} />

      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading || !settings || !alertsQuery.data ? (
        <QueryLoadingBlock />
      ) : (
        <section
          className="w-full max-w-[760px] overflow-hidden rounded-[14px] border"
          style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
        >
          <SettingsRow title={t('timezone.title')} description={t('timezone.description')}>
            <SettingsSelect
              ariaLabel={t('timezone.label')}
              value={settings.timezone}
              options={timezoneOptions}
              disabled={busy}
              onChange={(timezone) => saveSettings.mutate({ timezone })}
            />
          </SettingsRow>

          <SettingsRow
            title={t('defaultPlaylist.title')}
            description={t('defaultPlaylist.description')}
          >
            <SettingsSelect
              ariaLabel={t('defaultPlaylist.label')}
              value={settings.defaultPlaylistId ?? ''}
              options={playlistOptions}
              disabled={busy}
              onChange={(value) =>
                saveSettings.mutate({ defaultPlaylistId: value ? value : null })
              }
            />
          </SettingsRow>

          <SettingsRow
            title={t('syncInterval.title')}
            description={t('syncInterval.description')}
          >
            <SettingsChipGroup
              options={syncIntervalOptions}
              value={settings.syncIntervalSeconds}
              disabled={busy}
              onChange={(syncIntervalSeconds: SyncIntervalSeconds) =>
                saveSettings.mutate({ syncIntervalSeconds })
              }
            />
          </SettingsRow>

          <SettingsRow
            title={t('idleDisplay.title')}
            description={t('idleDisplay.description')}
          >
            <SettingsChipGroup
              options={idleDisplayOptions}
              value={settings.idleDisplay}
              disabled={busy}
              onChange={(idleDisplay: IdleDisplay) => saveSettings.mutate({ idleDisplay })}
            />
          </SettingsRow>

          <SettingsRow title={t('appearance.title')} description={t('appearance.description')}>
            <SettingsChipGroup
              options={[
                { label: t('appearance.dark'), value: 'dark' },
                { label: t('appearance.light'), value: 'light' },
              ]}
              value={theme}
              onChange={(next) => setTheme(next)}
            />
          </SettingsRow>

          <SettingsRow title={t('alerts.title')} description={t('alerts.description')} last>
            <label
              className="flex items-center gap-3 text-sm font-semibold"
              style={{ color: 'var(--gs-ink)' }}
            >
              <input
                type="checkbox"
                checked={alertsQuery.data.emailAlertsEnabled}
                disabled={busy}
                onChange={(event) => saveAlerts.mutate(event.target.checked)}
                className="h-4 w-4 rounded"
              />
              {t('alerts.label')}
            </label>
          </SettingsRow>
        </section>
      )}

      {saveError ? (
        <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }}>
          {saveError}
        </p>
      ) : null}

      {toast ? (
        <div
          className="fixed bottom-7 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2.5 rounded-xl border px-[18px] py-3 text-sm font-semibold"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
            animation: 'gs-in 200ms ease both',
          }}
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: 'var(--gs-ok)' }}
          />
          {toast}
        </div>
      ) : null}
    </div>
  );
}
